import React from "react";
import {Link} from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-gray-300">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/" className="text-purple-400 hover:text-purple-300 mb-8 inline-block">
          &larr; Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: January 26, 2026</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p>
              Welcome to Exo Entertainment ("we," "our," or "us"). We are committed to protecting your
              personal information and your right to privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our website and
              services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium text-white mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Account information (name, email address) when you sign up</li>
              <li>Profile information you choose to add</li>
              <li>Content you create (projects, pylons, notes)</li>
              <li>Communications with us (support requests, feedback)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-2 mt-4">2.2 Information from Third Parties</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Google Sign-In:</strong> When you authenticate with Google, we receive your
                name, email address, and profile picture. If you grant Google Drive access, we can
                create documents in your Drive on your behalf.
              </li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-2 mt-4">2.3 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Usage data (pages visited, features used)</li>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and approximate location</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To provide and maintain our services</li>
              <li>To authenticate your identity and manage your account</li>
              <li>To create Google Docs, Sheets, or Slides when you request (with your permission)</li>
              <li>To send you important updates about our services</li>
              <li>To respond to your inquiries and support requests</li>
              <li>To improve our services and develop new features</li>
              <li>To detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Google API Services</h2>
            <p className="mb-4">
              Our use of information received from Google APIs adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 underline">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <p className="mb-4">
              Specifically, when you connect your Google account:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We only request the permissions necessary to provide our services</li>
              <li>We use Google Drive access solely to create documents you explicitly request</li>
              <li>We do not share your Google data with third parties</li>
              <li>We do not use your Google data for advertising purposes</li>
              <li>You can revoke access at any time through your Google Account settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing and Disclosure</h2>
            <p className="mb-4">We do not sell your personal information. We may share your information only in these circumstances:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Service Providers:</strong> With trusted third parties who help us operate
                our services (hosting, email delivery, payment processing)
              </li>
              <li>
                <strong>Legal Requirements:</strong> If required by law or to protect our rights
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger or acquisition
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly agree to share information
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect
              your personal information, including encryption in transit and at rest, secure
              authentication, and regular security assessments. However, no method of transmission
              over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed
              to provide you services. You can request deletion of your account and associated data
              at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Your Rights</h2>
            <p className="mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate information</li>
              <li>Delete your personal information</li>
              <li>Export your data in a portable format</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent for data processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Children's Privacy</h2>
            <p>
              Our services are not intended for children under 13 years of age. We do not knowingly
              collect personal information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our privacy practices, please
              contact us at:
            </p>
            <p className="mt-4">
              <strong className="text-white">E. x .O. Entertainment Studios Inc.</strong>
              <br />
              Email: privacy@exoentertainment.ca
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <Link to="/terms" className="text-purple-400 hover:text-purple-300">
            View Terms of Service &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
